import { useState, useEffect } from 'react';

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  image: string;
  category: string;
  date: string;
  readTime: string;
  content: string;
  slug: string;
  dateISO: string;
  wordCount: number;
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://brushour.co.uk';

function injectBlogPostSchema(post: BlogPost) {
  // Remove any existing article schema
  document.querySelectorAll('script[data-blog-post-schema]').forEach((s) => s.remove());

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: {
      '@type': 'ImageObject',
      url: post.image,
      width: 800,
      height: 500,
    },
    datePublished: post.dateISO,
    dateModified: post.dateISO,
    author: {
      '@type': 'Organization',
      name: 'Brushour',
      url: SITE_URL,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Brushour',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/3d769dc1e01059223eb26668720a1761/e6c157647967ffb5fa62a9e4cb4fe039.png',
        width: 200,
        height: 60,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/blog#${post.slug}`,
    },
    url: `${SITE_URL}/blog#${post.slug}`,
    articleSection: post.category,
    wordCount: post.wordCount,
    keywords: `painting Watford, decorating Hertfordshire, ${post.category.toLowerCase()}, Brushour`,
    inLanguage: 'en-GB',
    isPartOf: {
      '@type': 'Blog',
      name: 'Brushour Painting & Decorating Blog',
      url: `${SITE_URL}/blog`,
    },
  };

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-blog-post-schema', 'true');
  script.text = JSON.stringify(schema);
  document.head.appendChild(script);
}

function removeBlogPostSchema() {
  document.querySelectorAll('script[data-blog-post-schema]').forEach((s) => s.remove());
}

export default function BlogGrid() {
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);

  const posts: BlogPost[] = [
    {
      id: 1,
      slug: 'top-10-interior-painting-tips-watford-2025',
      dateISO: '2025-01-15',
      wordCount: 620,
      title: 'Top 10 Interior Painting Tips for Watford Homeowners in 2025',
      excerpt: 'Transform your Watford home with these professional interior painting tips. Learn about the best paint finishes, color selection strategies, and preparation techniques used by Hertfordshire\'s leading decorators.',
      image: 'https://public.readdy.ai/ai/img_res/46d09580d604faaea82e4e6abdbeea03.jpg',
      category: 'Professional Tips',
      date: 'January 15, 2025',
      readTime: '8 min read',
      content: `
        <h2>Essential Interior Painting Tips for Watford Homes</h2>
        <p>As Watford's trusted painting and decorating specialists, we've compiled our top 10 interior painting tips to help homeowners achieve professional results. Whether you're in Bushey, Pinner, or anywhere across Hertfordshire, these techniques will transform your space.</p>

        <h3>1. Proper Surface Preparation is Key</h3>
        <p>In Watford's varied housing stock, from Victorian terraces to modern apartments, proper preparation makes all the difference. Clean walls thoroughly, fill any cracks or holes, and sand smooth before painting. This ensures a flawless finish that lasts.</p>

        <h3>2. Choose the Right Paint Finish</h3>
        <p>For Hertfordshire homes, we recommend:</p>
        <ul>
          <li><strong>Matt finish</strong> for ceilings and low-traffic areas</li>
          <li><strong>Eggshell</strong> for living rooms and bedrooms</li>
          <li><strong>Satin or semi-gloss</strong> for kitchens, bathrooms, and high-traffic areas</li>
        </ul>

        <h3>3. Invest in Quality Paint</h3>
        <p>Watford homeowners often ask about paint quality. Premium paints provide better coverage, durability, and color retention. At Brushour, we use professional-grade paints that withstand Hertfordshire's climate.</p>

        <h3>4. Use Proper Painting Techniques</h3>
        <p>Apply paint in a "W" pattern for even coverage. Start from the top and work down. Two thin coats always beat one thick coat for a professional finish in your Watford property.</p>

        <h3>5. Don't Skip the Primer</h3>
        <p>Primer is essential, especially for Watford's older properties. It seals surfaces, improves paint adhesion, and ensures true color representation.</p>

        <h3>6. Consider Lighting Conditions</h3>
        <p>Hertfordshire's natural light varies throughout the year. Test paint colors in different lighting conditions before committing. North-facing rooms in Watford benefit from warmer tones.</p>

        <h3>7. Protect Your Furnishings</h3>
        <p>Use quality drop cloths and painter's tape. Remove or cover furniture completely. This professional approach prevents damage and speeds up the painting process.</p>

        <h3>8. Maintain Proper Ventilation</h3>
        <p>Even with low-VOC paints, proper ventilation is crucial. Open windows and use fans to circulate air during and after painting your Watford home.</p>

        <h3>9. Plan Your Color Scheme</h3>
        <p>Create flow throughout your Hertfordshire home by coordinating colors between rooms. Use the 60-30-10 rule: 60% dominant color, 30% secondary, 10% accent.</p>

        <h3>10. Know When to Call Professionals</h3>
        <p>While DIY painting works for small projects, larger jobs in Watford homes benefit from professional expertise. High ceilings, intricate trim work, and exterior painting are best left to experienced Hertfordshire decorators like Brushour.</p>

        <h3>Why Choose Brushour for Your Watford Painting Project?</h3>
        <p>With years of experience serving Watford, Bushey, Pinner, Rickmansworth, and surrounding Hertfordshire areas, Brushour delivers exceptional results. Our team understands local architecture, climate considerations, and homeowner preferences.</p>

        <p><strong>Ready to transform your Watford home?</strong> Contact Brushour today for a free quote. We serve all of Hertfordshire with professional painting and decorating services that exceed expectations.</p>
      `
    },
    {
      id: 2,
      slug: 'diy-painting-mistakes-hertfordshire-homes',
      dateISO: '2025-01-10',
      wordCount: 580,
      title: 'DIY Painting Guide: Common Mistakes to Avoid in Hertfordshire Homes',
      excerpt: 'Planning a DIY painting project in your Hertfordshire home? Discover the most common painting mistakes homeowners make and how to avoid them. Expert advice from Brushour\'s professional decorators.',
      image: 'https://readdy.ai/api/search-image?query=homeowner%20doing%20DIY%20painting%20project%20with%20paint%20roller%20and%20supplies%20in%20Hertfordshire%20home%20showing%20common%20painting%20mistakes%20and%20corrections%20helpful%20guide%20demonstration%20with%20paint%20cans%20brushes%20and%20preparation%20materials&width=800&height=500&seq=blog-post-002&orientation=landscape',
      category: 'DIY Guides',
      date: 'January 10, 2025',
      readTime: '6 min read',
      content: `
        <h2>Avoid These Common DIY Painting Mistakes</h2>
        <p>Many Hertfordshire homeowners attempt DIY painting to save money, but common mistakes can cost more in the long run. As Watford's leading painting professionals, we've seen it all. Here's how to avoid the most frequent pitfalls.</p>

        <h3>Mistake 1: Skipping Surface Preparation</h3>
        <p>The biggest mistake in Watford DIY projects is inadequate preparation. Rushing to paint without proper cleaning, filling, and sanding leads to poor adhesion and visible imperfections.</p>
        <p><strong>Solution:</strong> Dedicate at least 50% of your project time to preparation. Clean walls with sugar soap, fill holes with quality filler, and sand smooth. This is especially important in older Hertfordshire properties.</p>

        <h3>Mistake 2: Using Cheap Materials</h3>
        <p>Budget paint and tools seem economical but require more coats and deliver inferior results. Watford homeowners often end up repainting sooner, costing more overall.</p>
        <p><strong>Solution:</strong> Invest in quality paint, brushes, and rollers. Professional-grade materials provide better coverage and durability for your Hertfordshire home.</p>

        <h3>Mistake 3: Ignoring Weather Conditions</h3>
        <p>Hertfordshire's climate affects painting success. High humidity, extreme temperatures, or rain can ruin exterior painting projects and affect interior drying times.</p>
        <p><strong>Solution:</strong> Check weather forecasts for Watford area. Paint when temperatures are between 10-25°C with low humidity. Avoid painting in direct sunlight or before rain.</p>

        <h3>Mistake 4: Poor Tape Application</h3>
        <p>Incorrectly applied painter's tape causes bleeding and uneven lines, common in Watford DIY projects.</p>
        <p><strong>Solution:</strong> Press tape firmly along edges. Remove tape while paint is slightly wet for clean lines. Use quality painter's tape designed for your surface type.</p>

        <h3>Mistake 5: Overloading Brushes and Rollers</h3>
        <p>Too much paint causes drips, runs, and uneven coverage. This wastes paint and creates extra work in your Hertfordshire home.</p>
        <p><strong>Solution:</strong> Load brushes and rollers moderately. Tap off excess paint. Apply thin, even coats rather than one thick coat.</p>

        <h3>Mistake 6: Painting in Wrong Order</h3>
        <p>Many Watford DIYers paint walls before ceilings or forget to paint trim last, creating extra work and messy results.</p>
        <p><strong>Solution:</strong> Follow this order: ceiling, walls, trim. This prevents drips on finished surfaces and ensures professional results.</p>

        <h3>Mistake 7: Not Testing Colors</h3>
        <p>Choosing paint colors from small chips leads to disappointment. Colors look different in Hertfordshire homes depending on lighting and room size.</p>
        <p><strong>Solution:</strong> Buy sample pots and test on large areas. Observe colors at different times of day in your Watford property before committing.</p>

        <h3>Mistake 8: Rushing Between Coats</h3>
        <p>Impatience causes smudging and poor adhesion. Hertfordshire's humidity affects drying times.</p>
        <p><strong>Solution:</strong> Follow manufacturer's drying time recommendations. Add extra time in humid conditions. Patience ensures better results.</p>

        <h3>Mistake 9: Inadequate Ventilation</h3>
        <p>Poor air circulation extends drying time and creates unhealthy conditions in your Watford home.</p>
        <p><strong>Solution:</strong> Open windows and use fans. Ensure good airflow throughout your Hertfordshire property during and after painting.</p>

        <h3>Mistake 10: Attempting Complex Projects</h3>
        <p>Some projects exceed DIY capabilities. High ceilings, intricate trim, and exterior work in Watford homes require professional expertise.</p>
        <p><strong>Solution:</strong> Know your limits. For complex projects in Hertfordshire, hire experienced professionals like Brushour who guarantee quality results.</p>

        <h3>When to Call Brushour</h3>
        <p>If your Watford painting project involves:</p>
        <ul>
          <li>High ceilings or difficult access areas</li>
          <li>Exterior painting requiring scaffolding</li>
          <li>Multiple rooms or whole-house painting</li>
          <li>Specialized finishes or techniques</li>
          <li>Time constraints or tight deadlines</li>
        </ul>
        <p>Contact Brushour for professional service across Watford, Bushey, Pinner, Rickmansworth, and all Hertfordshire areas. We deliver flawless results without the DIY stress.</p>
      `
    },
    {
      id: 3,
      slug: 'choosing-paint-colors-watford-home-guide',
      dateISO: '2025-01-05',
      wordCount: 750,
      title: 'Choosing the Perfect Paint Colors for Your Watford Home: A Complete Guide',
      excerpt: 'Selecting paint colors for your Hertfordshire property can be overwhelming. Our expert guide covers color psychology, trending palettes, and how to choose colors that enhance your Watford home\'s unique character.',
      image: 'https://readdy.ai/api/search-image?query=beautiful%20paint%20color%20palette%20samples%20and%20swatches%20arranged%20on%20table%20in%20bright%20Watford%20home%20interior%20with%20color%20charts%20paint%20brushes%20and%20decorating%20materials%20showing%20various%20paint%20colors%20and%20finishes%20for%20home%20decoration&width=800&height=500&seq=blog-post-003&orientation=landscape',
      category: 'Home Improvement',
      date: 'January 5, 2025',
      readTime: '10 min read',
      content: `
        <h2>The Ultimate Guide to Choosing Paint Colors for Watford Homes</h2>
        <p>Selecting the perfect paint colors transforms your Hertfordshire home. As Watford's trusted painting experts, Brushour helps homeowners navigate color choices that enhance their property's beauty and value.</p>

        <h3>Understanding Color Psychology</h3>
        <p>Colors affect mood and perception. In Watford homes, consider these psychological effects:</p>
        <ul>
          <li><strong>Blue:</strong> Calming and serene, perfect for bedrooms and bathrooms in Hertfordshire properties</li>
          <li><strong>Green:</strong> Refreshing and balanced, ideal for living spaces and home offices</li>
          <li><strong>Warm neutrals:</strong> Welcoming and versatile, excellent for open-plan Watford homes</li>
          <li><strong>Grey:</strong> Sophisticated and modern, popular in contemporary Hertfordshire interiors</li>
          <li><strong>White:</strong> Clean and spacious, maximizes light in smaller Watford rooms</li>
        </ul>

        <h3>Consider Your Home's Architecture</h3>
        <p>Hertfordshire features diverse architectural styles. Match colors to your property type:</p>
        
        <h4>Victorian and Edwardian Homes (Common in Watford)</h4>
        <p>These period properties suit rich, heritage colors. Deep greens, burgundy, and navy complement original features. Pair with crisp white trim for authentic character.</p>

        <h4>1930s Semi-Detached Houses (Popular in Hertfordshire)</h4>
        <p>These homes work well with warm neutrals, soft pastels, and classic combinations. Cream, beige, and sage green enhance their timeless appeal.</p>

        <h4>Modern Properties (New Developments in Watford)</h4>
        <p>Contemporary homes embrace bold choices. Charcoal grey, pure white, and accent walls create striking modern interiors popular in Hertfordshire.</p>

        <h3>Hertfordshire's Natural Light Considerations</h3>
        <p>Light affects color appearance in your Watford home:</p>
        
        <h4>North-Facing Rooms</h4>
        <p>Receive cooler, indirect light. Choose warm colors like cream, warm grey, or soft yellow to counteract coolness in Hertfordshire properties.</p>

        <h4>South-Facing Rooms</h4>
        <p>Enjoy abundant warm light. These Watford rooms handle cooler colors like blue, green, or cool grey beautifully.</p>

        <h4>East-Facing Rooms</h4>
        <p>Bright morning light, cooler afternoons. Versatile spaces in Hertfordshire homes that work with most color palettes.</p>

        <h4>West-Facing Rooms</h4>
        <p>Warm afternoon and evening light. Perfect for rich, warm colors in your Watford property.</p>

        <h3>2025 Color Trends for Hertfordshire Homes</h3>
        <p>Current trends popular in Watford include:</p>
        <ul>
          <li><strong>Warm Neutrals:</strong> Greige, warm taupe, and soft beige create cozy Hertfordshire interiors</li>
          <li><strong>Earthy Tones:</strong> Terracotta, clay, and ochre bring natural warmth to Watford homes</li>
          <li><strong>Sage Green:</strong> This versatile shade suits both traditional and modern Hertfordshire properties</li>
          <li><strong>Soft Blues:</strong> From powder blue to deeper navy, blues remain popular in Watford</li>
          <li><strong>Warm Greys:</strong> Moving away from cool greys, warmer tones create inviting spaces</li>
        </ul>

        <h3>Creating Color Flow Throughout Your Watford Home</h3>
        <p>Achieve cohesion in your Hertfordshire property:</p>
        
        <h4>The 60-30-10 Rule</h4>
        <p>Use 60% dominant color (walls), 30% secondary color (furniture, curtains), and 10% accent color (accessories, cushions) in each Watford room.</p>

        <h4>Connecting Spaces</h4>
        <p>Use consistent undertones throughout your Hertfordshire home. If your living room has warm grey, continue warm tones in adjacent spaces.</p>

        <h4>Transition Areas</h4>
        <p>Hallways and landings in Watford homes benefit from neutral colors that complement adjoining rooms.</p>

        <h3>Testing Colors in Your Hertfordshire Home</h3>
        <p>Never skip this crucial step:</p>
        <ol>
          <li>Purchase sample pots of your chosen colors</li>
          <li>Paint large test patches (at least 60cm x 60cm) on different walls in your Watford room</li>
          <li>Observe colors at different times of day and in various lighting conditions</li>
          <li>Live with samples for several days before deciding</li>
          <li>Consider how colors look against your existing furnishings and flooring</li>
        </ol>

        <h3>Exterior Color Choices for Watford Properties</h3>
        <p>Exterior painting requires additional considerations:</p>
        <ul>
          <li><strong>Neighborhood harmony:</strong> Consider surrounding Hertfordshire properties</li>
          <li><strong>Architectural style:</strong> Respect your Watford home's character</li>
          <li><strong>Planning restrictions:</strong> Some Hertfordshire areas have conservation rules</li>
          <li><strong>Weather resistance:</strong> Choose durable paints for British climate</li>
          <li><strong>Curb appeal:</strong> Select colors that enhance your property's value</li>
        </ul>

        <h3>Professional Color Consultation from Brushour</h3>
        <p>Choosing colors feels overwhelming for many Watford homeowners. Brushour offers professional color consultation services throughout Hertfordshire. Our experienced team:</p>
        <ul>
          <li>Assesses your property's unique characteristics</li>
          <li>Considers your lifestyle and preferences</li>
          <li>Recommends colors that enhance your Watford home</li>
          <li>Provides sample testing and visualization</li>
          <li>Ensures cohesive color schemes throughout your property</li>
        </ul>

        <h3>Ready to Transform Your Hertfordshire Home?</h3>
        <p>Whether you need help choosing colors or want professional painting services, Brushour serves Watford, Bushey, Pinner, Rickmansworth, Chorleywood, Northwood, Radlett, Gerrards Cross, and surrounding Hertfordshire areas.</p>
        
        <p><strong>Contact us today</strong> for a free consultation and quote. Let Brushour's expertise bring your color vision to life with flawless execution and attention to detail.</p>
      `
    }
  ];

  useEffect(() => {
    if (selectedPost) {
      injectBlogPostSchema(selectedPost);
    } else {
      removeBlogPostSchema();
    }
    return () => {
      removeBlogPostSchema();
    };
  }, [selectedPost]);

  return (
    <>
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <article
                key={post.id}
                className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer group"
                onClick={() => setSelectedPost(post)}
              >
                <div className="relative w-full h-64 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    title={post.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="inline-block px-4 py-2 bg-sky-600 text-white text-sm font-medium rounded-full">
                      {post.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-slate-600 mb-3">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {post.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {post.readTime}
                    </span>
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-sky-600 transition-colors duration-300">
                    {post.title}
                  </h2>
                  <p className="text-slate-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center text-sky-600 font-medium group-hover:gap-2 transition-all duration-300">
                    <span>Read More</span>
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Article Modal */}
      {selectedPost && (
        <div
          className="fixed inset-0 z-50 bg-black/80 overflow-y-auto cursor-pointer"
          onClick={() => setSelectedPost(null)}
        >
          <div className="min-h-screen px-4 py-12">
            <div
              className="max-w-4xl mx-auto bg-white rounded-lg shadow-2xl cursor-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="sticky top-4 float-right mr-4 mt-4 w-12 h-12 flex items-center justify-center bg-slate-100 hover:bg-slate-200 rounded-full text-slate-700 transition-colors duration-300 cursor-pointer z-10"
                onClick={() => setSelectedPost(null)}
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
              
              <div className="relative w-full h-96">
                <img
                  src={selectedPost.image}
                  alt={selectedPost.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                  <span className="inline-block px-4 py-2 bg-sky-600 text-sm font-medium rounded-full mb-4">
                    {selectedPost.category}
                  </span>
                  <h1 className="text-4xl font-bold mb-4">{selectedPost.title}</h1>
                  <div className="flex items-center gap-6 text-sm">
                    <span className="flex items-center gap-2">
                      <i className="ri-calendar-line"></i>
                      {selectedPost.date}
                    </span>
                    <span className="flex items-center gap-2">
                      <i className="ri-time-line"></i>
                      {selectedPost.readTime}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-8 md:p-12">
                <div
                  className="prose prose-lg max-w-none prose-headings:text-slate-900 prose-h2:text-3xl prose-h2:font-bold prose-h2:mt-8 prose-h2:mb-4 prose-h3:text-2xl prose-h3:font-bold prose-h3:mt-6 prose-h3:mb-3 prose-h4:text-xl prose-h4:font-bold prose-h4:mt-4 prose-h4:mb-2 prose-p:text-slate-700 prose-p:leading-relaxed prose-p:mb-4 prose-ul:my-4 prose-li:text-slate-700 prose-strong:text-slate-900 prose-strong:font-bold"
                  dangerouslySetInnerHTML={{ __html: selectedPost.content }}
                />

                <div className="mt-12 pt-8 border-t border-slate-200">
                  <div className="bg-sky-50 rounded-lg p-6">
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">
                      Need Professional Painting Services in Watford?
                    </h3>
                    <p className="text-slate-700 mb-4">
                      Contact Brushour today for expert painting and decorating services across Hertfordshire. Free quotes, professional results, and exceptional customer service.
                    </p>
                    <div className="flex flex-wrap gap-4">
                      <a
                        href="tel:07951878168"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-sky-600 text-white font-medium rounded-lg hover:bg-sky-700 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-phone-line"></i>
                        Call Now
                      </a>
                      <a
                        href="/booking"
                        className="inline-flex items-center gap-2 px-6 py-3 bg-white text-sky-600 font-medium rounded-lg border-2 border-sky-600 hover:bg-sky-50 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-calendar-check-line"></i>
                        Book Consultation
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}